import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { BaseComponent } from '../components/base.component';
import { MessageService } from 'primeng/api';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SharedUserService } from '../services/shared-user.service';
import { CustomValidator } from '../custom-validator';
import { Constants } from '../constants';
import { UserResponseModel } from '../models/response/user-response-model';
import { takeUntil, finalize } from 'rxjs/operators';
import { WebStorageService } from '../services/web-storage.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ResultOfT } from 'src/app/shared/models/response/result';
import { AuthenticationService } from '../services/authentication.service';
import { ConfigResponseModel } from '../models/response/client-config-response';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FeatureFlags } from '../feature-flags';
import { FeatureFlagService } from '../services/feature-flag.service';

@Component({
  selector: 'app-shared-set-password',
  templateUrl: './shared-set-password.component.html',
  styleUrls: ['./shared-set-password.component.sass'],
  standalone: false
})
export class SharedSetPasswordComponent extends BaseComponent implements OnInit, OnDestroy {
  configResponseModel: ConfigResponseModel;
  imageUrl: SafeResourceUrl;
  private domainUrl: string = '';
  imageData: string;
  imagealt: string;
  imageTitle: string;
  private unsubscriber = new Subject<boolean>();
  title = 'Set Password';
  email: string;
  resetToken: string;
  registrationstatus: string;

  formFields = {
    email: 'email',
    newPassword: 'newPassword',
    confirmPassword: 'confirmPassword'
  };

  validationMessages = {
    newPassword: {
      required: 'Password is required',
      minlength: 'Password should be at least 6 characters',
      invalid: 'Enter valid password'
    },
    confirmPassword: {
      required: 'Password is required',
      invalid: 'Enter valid password',
      minlength: 'Password should be at least 6 characters',
      validPasswordMatch: 'Password Doesn\'t match'
    }
  };

  passwordStrength: string = '';

  validationRules = [
    { key: 'validLength', message: 'At least 12 characters length.' },
    { key: 'validUpperCase', message: 'At least one uppercase letter.' },
    { key: 'validLowerCase', message: 'At least one lowercase letter.' },
    { key: 'validNumber', message: 'At least one number.' },
    { key: 'validSpecialChar', message: 'At least one special character.' }
  ];

  validUpperCase: boolean = false;
  validLowerCase: boolean = false;
  validNumber: boolean = false;
  validSpecialChar: boolean = false;
  validLength: boolean = false;
  passwordSecurity: boolean = false;

  constructor(private formBuilder: UntypedFormBuilder,
    private router: Router,
    private authenticationService: AuthenticationService,
    private activatedRoute: ActivatedRoute,
    private userService: SharedUserService,
    private webStorageService: WebStorageService,
    private sanitizer: DomSanitizer,
    private featureFlagService: FeatureFlagService,
    messageService: MessageService) {
    super(messageService);
    this.activatedRoute.params
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((params: Params) => {
        this.email = params['emailId'];
        this.resetToken = params['resetToken'];
        this.registrationstatus = params['registrationstatus'];
      });

    this.configResponseModel = this.webStorageService.getClientConfig();
    if (this.configResponseModel) {
      this.imageData = 'data:image/png;base64, ' + this.configResponseModel.companyLogo;
      this.imageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.imageData);
      this.imagealt = this.configResponseModel.companyName;
      this.imageTitle = this.configResponseModel.companyName;
    }
    else {
      this.domainUrl = location.origin.replace("http://", "").replace("https://", "");
      this.authenticationService.getClinetDetails(this.domainUrl, 4).pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
        .subscribe((result: ResultOfT<ConfigResponseModel>) => {
          this.processResult<ConfigResponseModel>(result, () => {
            if (result.isSuccess) {
              if (result.responseModel) {
                this.imageData = 'data:image/png;base64, ' + result.responseModel.companyLogo;
                this.imageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.imageData);
                this.imagealt = result.responseModel.companyName;
                this.imageTitle = result.responseModel.companyName;
                this.webStorageService.storeClientConfig(result.responseModel);
              }
            }
          });
        });
    }
  }

  ngOnInit() {
    this.checkPasswordFeature();
    this.buildForm();
  }

  ngOnDestroy() {
    if (this.unsubscriber) {
      this.unsubscriber.next();
      this.unsubscriber.complete();
    }
  }

  checkPasswordFeature() {
    this.featureFlagService.getFeatureFlag(FeatureFlags.PasswordSecurity).subscribe(x => {
      this.passwordSecurity = x.responseModel.isEnabled;

      if (this.passwordSecurity) {
        this.validationMessages = {
          newPassword: {
            required: 'Password is required',
            minlength: 'Password should be at least 12 characters',
            invalid: 'Enter valid password'
          },
          confirmPassword: {
            required: 'Password is required',
            invalid: 'Enter valid password',
            minlength: 'Password should be at least 12 characters',
            validPasswordMatch: 'Password Doesn\'t match'
          }
        }
      }
      this.buildForm();
    });
  }

  onSubmit() {
    if (this.form.invalid) {
      this.markFormAsTouched();
      return;
    }
    this.loading = true;
    const userResponseModel = new UserResponseModel();
    userResponseModel.appUserpassword = this.form.get(this.formFields.newPassword).value;
    userResponseModel.appUserEmail = this.email;
    userResponseModel.resetToken = this.resetToken;
    if (this.registrationstatus)
      userResponseModel.appUserRegistrationStatus = this.registrationstatus;

    this.userService.setPassword(userResponseModel)
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe((result) => {
        if (result.isSuccess) {
          this.loading = false;
          this.showSuccessMessage("Password Set Successfully.");
          this.router.navigate(['/login']);
        }
        else {
          this.showInfoMessage(result.message);
        }
      }, (httpResponseError) => {
        this.showErrorMessage(httpResponseError.message);
      });
  }

  private buildForm() {
    const formGroup = this.formBuilder.group({});
    
    // Always add email field for consistency
    formGroup.addControl(this.formFields.email, this.formBuilder.control(this.email));
    
    if (this.passwordSecurity) {
      formGroup.addControl(this.formFields.newPassword, this.formBuilder.control('', {
        validators: [
          Validators.required, CustomValidator.whitespace,
          this.passwordComplexityValidator(),
        ], updateOn:'change'
      }));

      formGroup.addControl(this.formFields.confirmPassword, this.formBuilder.control('', {
        validators: [Validators.required, CustomValidator.whitespace], 
        updateOn: 'change' // Change to 'change' for immediate validation
      }));
    }
    else {
      formGroup.addControl(this.formFields.newPassword, this.formBuilder.control('', { 
        validators: [Validators.required, CustomValidator.whitespace, CustomValidator.passwordPattern], 
        updateOn: Constants.formControlUpdateStrategy.blur 
      }));
      
      formGroup.addControl(this.formFields.confirmPassword, this.formBuilder.control('', { 
        validators: [Validators.required, CustomValidator.whitespace, CustomValidator.passwordPattern], 
        updateOn: Constants.formControlUpdateStrategy.blur 
      }));
    }
    
    // Set password match validator for both cases
    formGroup.setValidators(CustomValidator.passwordMatch('newPassword', 'confirmPassword'));
    this.form = formGroup;
  }

  passwordComplexityValidator() {
    return (control: any) => {
      const value = control.value || '';
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumber = /\d/.test(value);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
      const isValidLength = value.length >= 12;

      this.validUpperCase = hasUpperCase;
      this.validLowerCase = hasLowerCase;
      this.validNumber = hasNumber;
      this.validSpecialChar = hasSpecialChar;
      this.validLength = isValidLength;

      if (hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && isValidLength) {
        return null;
      }

      return { passwordComplexity: true };
    };
  }

  passwordMatchValidator(group: UntypedFormGroup) {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { validPasswordMatch: true };
  }

  onPasswordInput() {
    const password = this.form.get('newPassword')?.value || '';
    this.passwordStrength = this.calculatePasswordStrength(password);
  }

  calculatePasswordStrength(password: string): string {
    const lengthScore = password.length >= 12 ? 1 : 0;
    const complexityScore = [
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /\d/.test(password),
      /[!@#$%^&*(),.?":{}|<>]/.test(password)
    ].filter(Boolean).length;

    const totalScore = lengthScore + complexityScore;

    if (totalScore <= 2) return 'Weak';
    if (totalScore <= 4 && totalScore >= 2) return 'Medium';
    return 'Strong';
  }

}
